import * as React from 'react';
import { Search } from 'lucide-react';
import { Input } from '@ooh/ui/components/input';
import { ExpandingSearchInput } from '@ooh/ui/components/expanding-search-input';

interface InventorySearchInputProps {
  /** Current search term from the URL (source of truth). */
  value: string;
  /** Debounced setter that writes the term to the URL. */
  onChange: (next: string) => void;
  placeholder: string;
  ariaLabel: string;
  /** Debounce delay in ms before writing to the URL. */
  debounceMs?: number;
  /**
   * When true, renders via `ExpandingSearchInput` (grows on focus without
   * reflowing siblings) instead of the plain fixed-width box below. Opt-in
   * per consumer — pass this only where the validated design calls for the
   * animation (currently the media-units catalog toolbar). Defaults to
   * false so other consumers (map search, offer units-tab) keep their
   * current fixed-width rendering unless explicitly opted in.
   */
  expandOnFocus?: boolean;
}

/**
 * Toolbar free-text search ("lupka") shared by the media-units catalog, the
 * map search header, and the offer units-tab (catalog filter-set parity).
 *
 * Lives in `src/components/` (not a feature folder) because it is consumed by
 * three features (`media-units`, `map-search`, `offers`) and cross-feature
 * imports are forbidden (`docs/WEB_ARCHITECTURE.md:36`).
 *
 * Owns local input state for responsive typing and debounces writes back to the URL.
 * The URL `value` is the source of truth: it re-syncs the input when it changes
 * externally (refresh, shared link, clear-all), so a refresh restores the term.
 */
export function InventorySearchInput({
  value,
  onChange,
  placeholder,
  ariaLabel,
  debounceMs = 300,
  expandOnFocus = false,
}: InventorySearchInputProps) {
  const [local, setLocal] = React.useState(value);
  const onChangeRef = React.useRef(onChange);
  onChangeRef.current = onChange;

  // Re-sync from the URL when it changes outside of typing (refresh, clear-all, nav).
  React.useEffect(() => {
    setLocal(value);
  }, [value]);

  const handleChange = React.useCallback((next: string) => {
    setLocal(next);
  }, []);

  // Debounce the write to the URL whenever the local value diverges from the URL value.
  React.useEffect(() => {
    if (local === value) return;
    const timer = window.setTimeout(() => onChangeRef.current(local), debounceMs);
    return () => window.clearTimeout(timer);
  }, [local, value, debounceMs]);

  if (expandOnFocus) {
    return (
      <ExpandingSearchInput
        type="search"
        value={local}
        onChange={(e) => handleChange(e.target.value)}
        placeholder={placeholder}
        aria-label={ariaLabel}
        data-testid="inventory-search-input"
      />
    );
  }

  return (
    <div className="relative w-full max-w-xs">
      <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
      <Input
        type="search"
        value={local}
        onChange={(e) => handleChange(e.target.value)}
        placeholder={placeholder}
        aria-label={ariaLabel}
        data-testid="inventory-search-input"
        className="pl-8"
      />
    </div>
  );
}
